import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSseInvalidate } from "@/lib/use-sse";

/**
 * Mail on the live pipe (#1465 step 3; moved here for Mailroom v2 PR 2).
 *
 * The server says "mail changed in your org" (`mail:changed` — a sync landed,
 * someone handled a thread) and every ["mailbox", …] query re-reads: the
 * toolbar badge, the slide-over, the Mailroom lanes and the open conversation.
 *
 * One subscriber at a time does the re-reading. The toolbar holds it on every
 * page EXCEPT the Mailroom, where the page holds it itself, so the room never
 * depends on the toolbar being mounted and a mail event never triggers two
 * rounds of refetches.
 */

/**
 * Floor between live-driven mailbox refetches (#1510).
 *
 * The server already coalesces NOTIFY bursts into one `mail:changed` per org
 * per ~400ms (services/liveChanges.ts). That is the right window for the Grid,
 * but a steady drip of mail meant every open office tab re-ran the mailbox
 * queries two and a half times a second; on 2026-09-14 that turned one
 * expensive endpoint into a site-wide outage. Three seconds still reads as
 * instant to a person watching mail land. The 30–60 s polling floor
 * underneath (lib/live-query.ts) is unchanged, so nothing depends on an event
 * arriving at all.
 */
export const MAIL_REFETCH_THROTTLE_MS = 3_000;

/**
 * Leading-edge throttle with a trailing catch-up: the first call goes straight
 * through (live stays live), calls inside the window are collapsed into one
 * that fires when it closes (nothing is silently dropped, so the last change
 * always lands).
 */
export function useThrottledCallback(fn: () => void, waitMs: number): () => void {
    const fnRef = useRef(fn);
    fnRef.current = fn;
    const lastRunRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        [],
    );

    return useCallback(() => {
        const since = Date.now() - lastRunRef.current;
        if (since >= waitMs) {
            lastRunRef.current = Date.now();
            fnRef.current();
            return;
        }
        if (timerRef.current) return; // already scheduled for the window's end
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            lastRunRef.current = Date.now();
            fnRef.current();
        }, waitMs - since);
    }, [waitMs]);
}

/** Re-read every mailbox query when the server says mail changed. */
export function useMailLiveRefresh(enabled: boolean): void {
    const qc = useQueryClient();
    const onMailChanged = useThrottledCallback(
        useCallback(() => {
            void qc.invalidateQueries({ queryKey: ["mailbox"] });
        }, [qc]),
        MAIL_REFETCH_THROTTLE_MS,
    );
    useSseInvalidate("mail:changed", onMailChanged, enabled);
}

/** True on the Mailroom's own pages, where the page holds the subscription. */
export function isMailroomPath(pathname: string): boolean {
    return pathname === "/mailroom" || pathname.startsWith("/mailroom/");
}
